/**
 * RollingPlanConfigModal — spec §4.6 / §5.6: per-project rolling adaptive-engine
 * configuration. Mode (off/proposal/silent), the working-day re-flow window, the
 * damping thresholds (variance % + drift days), and the progress signal. Gated on
 * `epcProject.update`; PUTs via usePutRollingConfig.
 * Visual register: Sovereign Emerald (Antd 6).
 */
import { Button, InputNumber, Modal, Radio, Select, Spin, message } from 'antd';
import { useEffect, useState } from 'react';
import { SOVEREIGN } from '../../utils/epcProjectVisual';
import { usePutRollingConfig, useRollingConfig } from '../hooks/useRollingPlan';
import type { RollingPlanConfig } from '../types';

export interface RollingPlanConfigModalProps {
    open: boolean;
    projectId: string;
    onClose: () => void;
}

const DEFAULTS: RollingPlanConfig = {
    mode: 'off',
    windowDays: 10,
    varianceThresholdPct: 10,
    driftThresholdDays: 2,
    progressSignal: 'auto',
};

const MODE_OPTIONS: { value: RollingPlanConfig['mode']; label: string; hint: string }[] = [
    { value: 'off', label: 'Off', hint: 'No nightly roll' },
    { value: 'proposal', label: 'Propose', hint: 'DM reviews each morning' },
    { value: 'silent', label: 'Silent', hint: 'Auto-apply clean days' },
];

const SIGNAL_OPTIONS: { value: RollingPlanConfig['progressSignal']; label: string }[] = [
    { value: 'auto', label: 'Auto (claim → status → actual qty)' },
    { value: 'claim', label: 'Claim %' },
    { value: 'status', label: 'Done-status quantity' },
    { value: 'actualQty', label: 'Actual quantity' },
];

function apiErrMsg(err: unknown, fallback: string): string {
    const msg = (err as { response?: { data?: { message?: string } } } | undefined)?.response?.data
        ?.message;
    return msg || fallback;
}

const labelCls = 'text-[11px] font-bold uppercase tracking-[0.1em]';

export function RollingPlanConfigModal({ open, projectId, onClose }: RollingPlanConfigModalProps) {
    const configQ = useRollingConfig(projectId, open);
    const putMut = usePutRollingConfig(projectId);

    const [form, setForm] = useState<RollingPlanConfig>(DEFAULTS);

    // Hydrate the form from the loaded config each time the modal opens.
    useEffect(() => {
        if (open && configQ.data) setForm({ ...DEFAULTS, ...configQ.data });
    }, [open, configQ.data]);

    const set = <K extends keyof RollingPlanConfig>(key: K, value: RollingPlanConfig[K]) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    async function handleSave() {
        try {
            await putMut.mutateAsync(form);
            message.success('Rolling-plan configuration saved');
            onClose();
        } catch (e) {
            message.error(apiErrMsg(e, 'Save failed'));
        }
    }

    const disabled = form.mode === 'off';

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={560}
            destroyOnHidden
            maskClosable={false}
            title={
                <span className="text-[15px] font-bold" style={{ color: SOVEREIGN.title }}>
                    Rolling plan
                    <span
                        className="ml-2 text-[11.5px] font-semibold"
                        style={{ color: SOVEREIGN.kpiCaption }}
                    >
                        Adaptive nightly re-flow
                    </span>
                </span>
            }
            styles={{
                header: {
                    background: SOVEREIGN.canvas,
                    borderBottom: `1px solid ${SOVEREIGN.cardBorder}`,
                    paddingBottom: 12,
                },
                body: { background: SOVEREIGN.paper, padding: 20 },
                footer: {
                    background: SOVEREIGN.canvas,
                    borderTop: `1px solid ${SOVEREIGN.cardBorder}`,
                    paddingTop: 12,
                },
            }}
            footer={
                <div className="flex items-center justify-end gap-2">
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        type="primary"
                        loading={putMut.isPending}
                        onClick={() => void handleSave()}
                        style={{ background: SOVEREIGN.btn, borderColor: SOVEREIGN.btnBorder }}
                    >
                        Save
                    </Button>
                </div>
            }
        >
            {configQ.isPending ? (
                <div className="flex justify-center py-12">
                    <Spin />
                </div>
            ) : (
                <div className="space-y-5">
                    {/* Mode */}
                    <div>
                        <div className={labelCls} style={{ color: SOVEREIGN.kpiCaption }}>
                            Mode
                        </div>
                        <Radio.Group
                            className="mt-1.5"
                            value={form.mode}
                            onChange={(e) => set('mode', e.target.value)}
                            optionType="button"
                            buttonStyle="solid"
                        >
                            {MODE_OPTIONS.map((o) => (
                                <Radio.Button key={o.value} value={o.value}>
                                    {o.label}
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                        <div className="mt-1 text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
                            {MODE_OPTIONS.find((o) => o.value === form.mode)?.hint}
                        </div>
                    </div>

                    {/* Window + thresholds */}
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <div className={labelCls} style={{ color: SOVEREIGN.kpiCaption }}>
                                Window (days)
                            </div>
                            <InputNumber
                                className="mt-1.5 w-full"
                                min={5}
                                max={30}
                                disabled={disabled}
                                value={form.windowDays}
                                onChange={(v) => set('windowDays', v ?? DEFAULTS.windowDays)}
                            />
                        </div>
                        <div>
                            <div className={labelCls} style={{ color: SOVEREIGN.kpiCaption }}>
                                Variance %
                            </div>
                            <InputNumber
                                className="mt-1.5 w-full"
                                min={1}
                                max={50}
                                disabled={disabled}
                                value={form.varianceThresholdPct}
                                onChange={(v) =>
                                    set('varianceThresholdPct', v ?? DEFAULTS.varianceThresholdPct)
                                }
                            />
                        </div>
                        <div>
                            <div className={labelCls} style={{ color: SOVEREIGN.kpiCaption }}>
                                Drift (days)
                            </div>
                            <InputNumber
                                className="mt-1.5 w-full"
                                min={1}
                                max={10}
                                disabled={disabled}
                                value={form.driftThresholdDays}
                                onChange={(v) =>
                                    set('driftThresholdDays', v ?? DEFAULTS.driftThresholdDays)
                                }
                            />
                        </div>
                    </div>
                    <div className="text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
                        A task re-flows only when it is past the variance % <em>or</em> drift-days
                        threshold — sub-threshold noise is damped.
                    </div>

                    {/* Progress signal */}
                    <div>
                        <div className={labelCls} style={{ color: SOVEREIGN.kpiCaption }}>
                            Progress signal
                        </div>
                        <Select<RollingPlanConfig['progressSignal']>
                            className="mt-1.5 w-full"
                            disabled={disabled}
                            value={form.progressSignal}
                            onChange={(v) => set('progressSignal', v)}
                            options={SIGNAL_OPTIONS}
                        />
                    </div>
                </div>
            )}
        </Modal>
    );
}
